import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMe, startGoogleLogin } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Members-only gate. Requires:
 *   • Signed-in via Emergent Google Auth
 *   • Owns at least one purchase (Gumroad sale) OR a linked redemption code
 *
 * States:
 *   null       → checking
 *   "guest"    → not signed in → offer to sign in
 *   "outsider" → signed in but no purchases → offer to buy tokens or link a code
 *   "member"   → render children
 */
export default function ProtectedRoute({ children, returnTo }) {
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // If returning from OAuth, let AuthCallback finish first (it processes the
    // #session_id fragment and reloads).
    if (window.location.hash?.includes("session_id=")) {
      setStatus(null);
      return;
    }
    (async () => {
      const me = await fetchMe();
      if (!me) {
        setStatus("guest");
        return;
      }
      setUser(me);
      try {
        const res = await fetch(`${API}/api/auth/tokens`, {
          credentials: "include",
        });
        if (!res.ok) {
          setStatus("outsider");
          return;
        }
        const data = await res.json();
        setBalance(data.balance || 0);
        setStatus(data.has_access ? "member" : "outsider");
      } catch (e) {
        setStatus("outsider");
      }
    })();
  }, []);

  if (status === null) {
    return <GateScreen title="Verifying membership…" tone="loading" />;
  }

  if (status === "guest") {
    return (
      <GateScreen
        title="Members Only"
        tone="guest"
        message="This wing of the University is reserved for token holders. Please sign in to continue — if you already own tokens under this email, we'll auto-detect them."
      >
        <button
          type="button"
          data-testid="protected-signin-btn"
          onClick={() =>
            startGoogleLogin(returnTo || window.location.pathname || "/university")
          }
          style={pillStyle("solid")}
        >
          Sign in with Google
        </button>
        <button
          type="button"
          data-testid="protected-return-btn"
          onClick={() => navigate("/university")}
          style={pillStyle("outline")}
        >
          Return to the University
        </button>
      </GateScreen>
    );
  }

  if (status === "outsider") {
    return (
      <GateScreen
        title="Token Access Required"
        tone="outsider"
        message={
          user
            ? `You're signed in as ${user.name || user.email}, but we don't see any tokens under this account yet. Get a token pack from the Storefront, or paste a redemption code you already own.`
            : "Token access required."
        }
      >
        <LinkCodeForm onLinked={() => window.location.reload()} />
        <a
          href="https://next-xus.com"
          data-testid="protected-buy-btn"
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...pillStyle("solid"), textDecoration: "none" }}
        >
          Get $5 Access Token
        </a>
        <button
          type="button"
          data-testid="protected-profile-btn"
          onClick={() => navigate("/profile")}
          style={pillStyle("outline")}
        >
          Go to Profile
        </button>
      </GateScreen>
    );
  }

  // member
  return (
    <>
      <div
        data-testid="member-strip"
        style={{
          background: "rgba(212,175,55,.08)",
          borderBottom: "1px solid rgba(212,175,55,.35)",
          color: "gold",
          padding: ".55rem 1rem",
          fontFamily: "Georgia, serif",
          fontSize: 13,
          textAlign: "center",
          letterSpacing: ".08em",
          textTransform: "uppercase",
        }}
      >
        Member wing · {balance} token{balance === 1 ? "" : "s"} available ·{" "}
        <a
          href="/profile"
          data-testid="member-strip-profile-link"
          style={{ color: "gold", textDecoration: "underline" }}
        >
          Profile
        </a>
      </div>
      {children}
    </>
  );
}

function LinkCodeForm({ onLinked }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/auth/link-code`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Link failed (${res.status})`);
      }
      onLinked();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: ".5rem", alignItems: "center" }}>
      <label
        htmlFor="link-code-input"
        style={{
          color: "gold",
          fontFamily: "Georgia, serif",
          fontSize: 13,
          letterSpacing: ".12em",
          textTransform: "uppercase",
        }}
      >
        Or paste a redemption code
      </label>
      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", justifyContent: "center" }}>
        <input
          id="link-code-input"
          data-testid="protected-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XX-000000"
          style={{
            background: "#111118",
            color: "#e0e0e0",
            border: "1px solid gold",
            padding: ".7rem 1rem",
            borderRadius: 6,
            fontFamily: "Georgia, serif",
            fontSize: 14,
            minWidth: 220,
          }}
        />
        <button
          type="submit"
          data-testid="protected-code-submit"
          disabled={busy || !code.trim()}
          style={pillStyle("outline")}
        >
          {busy ? "Linking…" : "Link Code"}
        </button>
      </div>
      {err ? (
        <p data-testid="protected-code-error" style={{ color: "#ff6b6b", fontSize: 13, margin: 0 }}>
          {err}
        </p>
      ) : null}
    </form>
  );
}

function GateScreen({ title, message, tone, children }) {
  return (
    <div
      data-testid={`gate-${tone}`}
      style={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0f",
        padding: "3rem 1.5rem",
      }}
    >
      <div
        style={{
          maxWidth: 640,
          textAlign: "center",
          border: "1px solid gold",
          borderRadius: 12,
          padding: "2.5rem 2rem",
          background: "#111118",
          boxShadow: "0 0 40px rgba(255,215,0,.15)",
        }}
      >
        <h1
          style={{
            color: "gold",
            fontFamily: "Georgia, serif",
            fontSize: "2rem",
            marginTop: 0,
            letterSpacing: ".06em",
          }}
        >
          {title}
        </h1>
        {message ? (
          <p style={{ color: "#e0e0e0", fontFamily: "Georgia, serif", fontSize: 15, lineHeight: 1.6 }}>
            {message}
          </p>
        ) : null}
        <div
          style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap",
            justifyContent: "center",
            marginTop: "1.5rem",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function pillStyle(variant) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 44,
    padding: "0 1.5rem",
    borderRadius: 999,
    fontFamily: "Georgia, serif",
    fontSize: 14,
    letterSpacing: ".12em",
    textTransform: "uppercase",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "1px solid gold",
  };
  if (variant === "solid") {
    return { ...base, background: "gold", color: "#0a0a0f" };
  }
  return { ...base, background: "transparent", color: "gold" };
}
